import {
  DASHBOARD_PERIODS,
  DASHBOARD_PERIOD_LABELS,
  DEFAULT_DASHBOARD_PERIOD,
  type DashboardOverview,
  type DashboardPeriod,
} from "@estelle/domain";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CalendarDays } from "lucide-react";
import { useState, type ReactNode } from "react";

import { ForbiddenError } from "@/adapters/api/base";
import { useDataSource } from "@/adapters/data-source";
import { ChartCard, FigureTable } from "@/components/charts/ChartCard";
import { TimeSeriesChart } from "@/components/charts/lazy";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { LoadFailed, NoAccess } from "@/components/shell/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCompactMoney, formatRupees } from "@/lib/chart-format";
import { reportingNow } from "@/lib/reportClock";
import { cn } from "@/lib/utils";
import { ActionCards } from "@/modules/dashboard/components/ActionCards";
import { Performance } from "@/modules/dashboard/components/Performance";
import { RtoBars } from "@/modules/dashboard/components/RtoBars";
import { SyncFooter } from "@/modules/dashboard/components/SyncFooter";
import { comparedWith } from "@/modules/dashboard/format";
import { orderCountNote } from "@/modules/dashboard/revenue";
import { summariseTrend } from "@/modules/dashboard/trend";
import { formatDate, formatMoney } from "@/modules/gst/format";
import { bucketDetail, rangeLabel } from "@/modules/reports/orderChart";

function SectionHead({
  id,
  title,
  note,
  children,
}: {
  id: string;
  title: string;
  note?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="flex flex-col gap-0.5">
        <h2
          id={id}
          className="text-muted-foreground text-xs font-semibold tracking-wider uppercase"
        >
          {title}
        </h2>
        {note ? <p className="text-muted-foreground text-xs">{note}</p> : null}
      </div>
      {children}
    </div>
  );
}

function PeriodSwitch({
  value,
  onChange,
}: {
  value: DashboardPeriod;
  onChange: (next: DashboardPeriod) => void;
}) {
  return (
    <div role="group" aria-label="Period" className="flex gap-1">
      {DASHBOARD_PERIODS.map((period) => (
        <Button
          key={period}
          type="button"
          size="xs"
          variant="outline"
          aria-pressed={value === period}
          className={cn(
            "h-8 px-3 pointer-coarse:h-11",
            value === period && SELECTED_CHOICE,
          )}
          onClick={() => {
            onChange(period);
          }}
        >
          {DASHBOARD_PERIOD_LABELS[period]}
        </Button>
      ))}
    </div>
  );
}

/**
 * The dates the figures below are for, and the dates they are compared with
 * (#681). Always on screen, so nobody has to work them out from "7 days".
 */
function AppliedDates({ overview }: { overview: DashboardOverview }) {
  const { current, previous } = overview.window;
  return (
    <p
      data-slot="applied-dates"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
    >
      <CalendarDays
        aria-hidden="true"
        className="text-muted-foreground size-4 shrink-0"
      />
      <span className="sr-only">Dates shown:</span>
      <span className="font-medium">
        {rangeLabel(current.from, current.to)}
      </span>
      <span className="text-muted-foreground">
        vs {rangeLabel(previous.from, previous.to)} · India time
      </span>
    </p>
  );
}

function Skeletons() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} className="h-28 w-full" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-40 w-full" />
        ))}
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-4 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function TrendChart({
  overview,
  period,
}: {
  overview: DashboardOverview;
  period: DashboardPeriod;
}) {
  const trend = overview.trend;
  if (!trend) return null;
  const hasRevenue = trend.every((day) => day.revenue !== null);
  const summary = summariseTrend(trend, period);
  const focusDay = summary.focus === null ? undefined : trend[summary.focus];
  const money = (value: number) => formatRupees(value);
  const figure = (day: (typeof trend)[number]) =>
    hasRevenue ? money(Number(day.revenue)) : orderCountNote(day.orders);
  const total = `${orderCountNote(summary.orders)}${
    summary.revenue === null ? "" : `, ${money(Number(summary.revenue))}`
  }`;
  const first = trend[0];
  const last = trend[trend.length - 1];
  const span = first && last ? rangeLabel(first.date, last.date) : "";

  return (
    <ChartCard
      title={hasRevenue ? "Orders and revenue" : "Orders"}
      description={
        <>
          {period === "today" ? (
            <>
              <span className="text-foreground font-medium">
                Today: {total}.
              </span>{" "}
              The week ending today is drawn for comparison.
            </>
          ) : (
            <>
              <span className="text-foreground font-medium">{total}</span> in
              the {DASHBOARD_PERIOD_LABELS[period].toLowerCase()}, {span}.
              {focusDay ? (
                <>
                  {" "}
                  Busiest day: {formatDate(focusDay.date)}, {figure(focusDay)}.
                </>
              ) : null}
            </>
          )}{" "}
          One bar per day, midnight to midnight IST; cancelled orders are
          counted, as in the cards above.
        </>
      }
      table={
        <FigureTable
          rows={trend}
          rowKey={(day) => day.date}
          columns={[
            { header: "Date", cell: (day) => formatDate(day.date) },
            { header: "Orders", numeric: true, cell: (day) => day.orders },
            ...(hasRevenue
              ? [
                  {
                    header: "Revenue",
                    numeric: true,
                    strong: true,
                    cell: (day: (typeof trend)[number]) =>
                      formatMoney(day.revenue ?? "0.00"),
                  },
                ]
              : []),
          ]}
        />
      }
    >
      <TimeSeriesChart
        className="h-72"
        points={trend.map((day) => ({
          label: formatDate(day.date),
          detail: bucketDetail(day.date, "day"),
          value: hasRevenue ? Number(day.revenue) : day.orders,
          note: hasRevenue ? orderCountNote(day.orders) : undefined,
        }))}
        valueLabel={hasRevenue ? "Revenue" : "Orders"}
        formatValue={(value) =>
          hasRevenue ? formatMoney(value.toFixed(2)) : String(value)
        }
        formatTick={(value) =>
          hasRevenue ? formatCompactMoney(value) : String(value)
        }
        highlight={
          summary.focus === null || !focusDay
            ? undefined
            : {
                index: summary.focus,
                label: hasRevenue
                  ? formatCompactMoney(Number(focusDay.revenue))
                  : String(focusDay.orders),
              }
        }
        ariaLabel={`Daily ${hasRevenue ? "revenue" : "orders"}, ${span}. ${total}.${
          focusDay && period !== "today"
            ? ` Busiest day ${formatDate(focusDay.date)}, ${figure(focusDay)}.`
            : ""
        }`}
      />
    </ChartCard>
  );
}

/**
 * The dashboard (#528): what needs doing today, how the business is doing, and
 * where the trouble is.
 *
 * Every card opens something. A card that counts work opens its list with the
 * filter it counted by; a card that shows a rate opens the report for the same
 * dates. One request fetches everything this person may see; the server keeps
 * it for about a minute, and the screen asks again whenever the tab regains
 * focus.
 *
 * The dashboard declares no fields (`ModuleUi` - it is not a filterable list),
 * so the toolbar's search, filter and column controls sit out.
 */
export function Overview() {
  const data = useDataSource();
  const [period, setPeriod] = useState<DashboardPeriod>(
    DEFAULT_DASHBOARD_PERIOD,
  );

  const query = useQuery({
    queryKey: ["dashboard", "overview", period],
    queryFn: () => data.dashboard.overview(period),
    // Keep the last figures up, dimmed, while the next period is counted.
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnWindowFocus: "always",
  });

  if (query.error instanceof ForbiddenError) {
    return (
      <NoAccess
        permission={query.error.permission ?? "dashboard.overview.view"}
      />
    );
  }

  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  if (query.isPending) return <Skeletons />;

  const overview = query.data;
  const now = reportingNow();
  const hasPerformance =
    overview.sales || overview.delivery || overview.mix || overview.speed;
  const shown = overview.window.current;

  return (
    <div
      className={cn(
        "flex flex-col gap-6 transition-opacity",
        query.isPlaceholderData && "opacity-60",
      )}
      aria-busy={query.isPlaceholderData}
    >
      <section aria-labelledby="needs-action" className="flex flex-col gap-3">
        <SectionHead
          id="needs-action"
          title="Needs action today"
          note="Click a card to open the list, already filtered."
        />
        <ActionCards
          cards={overview.actions}
          limits={overview.limits}
          now={now}
        />
      </section>

      {hasPerformance ? (
        <section aria-labelledby="how-are-we" className="flex flex-col gap-3">
          <SectionHead
            id="how-are-we"
            title="How are we doing"
            note={`${comparedWith(period)} Click a card to open its report for the same dates.`}
          >
            <PeriodSwitch value={period} onChange={setPeriod} />
          </SectionHead>
          <AppliedDates overview={overview} />
          <Performance overview={overview} />
        </section>
      ) : null}

      {overview.trend || overview.rtoByCourier || overview.rtoByState ? (
        <section aria-labelledby="charts" className="flex flex-col gap-3">
          <SectionHead id="charts" title="Charts" />
          <TrendChart overview={overview} period={period} />
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {overview.rtoByCourier ? (
              <RtoBars
                title="RTO rate by courier"
                description={`${DASHBOARD_PERIOD_LABELS[period]}, ${rangeLabel(shown.from, shown.to)} · amber over ${String(overview.limits.rto_rate.amber)}%, red over ${String(overview.limits.rto_rate.red)}%`}
                rows={overview.rtoByCourier}
                limit={overview.limits.rto_rate}
                noun="courier"
                empty="No shipment reached delivery or RTO in these dates."
              />
            ) : null}
            {overview.rtoByState ? (
              <RtoBars
                title="RTO rate by state"
                description={`${DASHBOARD_PERIOD_LABELS[period]}, ${rangeLabel(shown.from, shown.to)} · amber over ${String(overview.limits.rto_rate.amber)}%, red over ${String(overview.limits.rto_rate.red)}%`}
                rows={overview.rtoByState}
                limit={overview.limits.rto_rate}
                noun="state"
                label={(key) => key}
                empty="No shipment reached delivery or RTO in these dates."
              />
            ) : null}
          </div>
        </section>
      ) : null}

      <SyncFooter now={now} />
    </div>
  );
}
